export const SOUND_EFFECTS = [
  'tap',
  'menu-open',
  'choice-select',
  'mission-start',
  'record-complete',
  'mission-complete',
  'quiz-correct',
  'quiz-wrong',
  'quiz-retry',
  'world-open',
  'passport-stamp',
  'badge-unlock',
  'fridge-store',
  'daily-complete',
  'family-message',
  'family-cheer',
] as const;

export type SoundEffect = typeof SOUND_EFFECTS[number];

export interface SoundPreferences {
  enabled: boolean;
  volume: number;
  quietMode: boolean;
  hapticsEnabled: boolean;
  reducedMotion: boolean;
}

export const DEFAULT_SOUND_PREFERENCES: SoundPreferences = {
  enabled: true,
  volume: 0.58,
  quietMode: false,
  hapticsEnabled: true,
  reducedMotion: false,
};

export function normalizeSoundPreferences(value: Partial<SoundPreferences> | null | undefined): SoundPreferences {
  const volume = typeof value?.volume === 'number' && Number.isFinite(value.volume)
    ? Math.min(1, Math.max(0, value.volume))
    : DEFAULT_SOUND_PREFERENCES.volume;
  return {
    enabled: typeof value?.enabled === 'boolean' ? value.enabled : DEFAULT_SOUND_PREFERENCES.enabled,
    volume,
    quietMode: typeof value?.quietMode === 'boolean' ? value.quietMode : DEFAULT_SOUND_PREFERENCES.quietMode,
    hapticsEnabled: typeof value?.hapticsEnabled === 'boolean' ? value.hapticsEnabled : DEFAULT_SOUND_PREFERENCES.hapticsEnabled,
    reducedMotion: typeof value?.reducedMotion === 'boolean' ? value.reducedMotion : DEFAULT_SOUND_PREFERENCES.reducedMotion,
  };
}

type BrowserAudioContext = AudioContext;

type ToneOptions = {
  start: number;
  duration: number;
  frequency: number;
  endFrequency?: number;
  gain: number;
  type?: OscillatorType;
};

class MrmSoundEngine {
  private context: BrowserAudioContext | null = null;
  private master: GainNode | null = null;
  private unlocked = false;

  private getAudioContext(): BrowserAudioContext | null {
    if (typeof window === 'undefined') return null;
    const AudioContextConstructor = window.AudioContext
      ?? (window as typeof window & { webkitAudioContext?: typeof AudioContext }).webkitAudioContext;
    if (!AudioContextConstructor) return null;
    if (!this.context) {
      this.context = new AudioContextConstructor();
      this.master = this.context.createGain();
      this.master.gain.value = 0.0001;
      this.master.connect(this.context.destination);
    }
    return this.context;
  }

  async unlock(): Promise<boolean> {
    const context = this.getAudioContext();
    if (!context) return false;
    try {
      if (context.state === 'suspended') await context.resume();
      this.unlocked = context.state === 'running';
      return this.unlocked;
    } catch {
      return false;
    }
  }

  isUnlocked(): boolean {
    return this.unlocked;
  }

  async play(effect: SoundEffect, preferences: SoundPreferences): Promise<boolean> {
    if (!preferences.enabled || preferences.volume <= 0) return false;
    if (preferences.quietMode && ['tap','menu-open','choice-select','mission-start'].includes(effect)) return false;
    if (!this.unlocked && !(await this.unlock())) return false;
    const context = this.context;
    const master = this.master;
    if (!context || !master) return false;

    const quietMultiplier = preferences.quietMode ? 0.34 : 1;
    const masterVolume = Math.max(0.0001, preferences.volume * quietMultiplier * 0.33);
    const now = context.currentTime + 0.006;
    master.gain.cancelScheduledValues(now);
    master.gain.setValueAtTime(Math.max(0.0001, master.gain.value), now);
    master.gain.exponentialRampToValueAtTime(masterVolume, now + 0.015);
    master.gain.exponentialRampToValueAtTime(0.0001, now + this.durationFor(effect) + 0.32);

    switch (effect) {
      case 'tap':
        this.tone(context, master, { start: now, duration: 0.055, frequency: 760, endFrequency: 520, gain: 0.22, type: 'sine' });
        break;
      case 'menu-open':
        this.tone(context, master, { start: now, duration: 0.09, frequency: 410, endFrequency: 650, gain: 0.12, type: 'sine' });
        break;
      case 'choice-select':
        this.tone(context, master, { start: now, duration: 0.085, frequency: 560, endFrequency: 720, gain: 0.14, type: 'triangle' });
        this.tone(context, master, { start: now + 0.07, duration: 0.11, frequency: 880, gain: 0.08, type: 'sine' });
        break;
      case 'mission-start':
        this.sequence(context, master, now, [392, 523.25], 0.11, 0.13, 'triangle');
        this.noise(context, master, now + 0.09, 0.13, 0.022, 1800);
        break;
      case 'record-complete':
        this.noise(context, master, now, 0.09, 0.055, 1650);
        this.tone(context, master, { start: now + 0.02, duration: 0.14, frequency: 620, endFrequency: 760, gain: 0.22, type: 'triangle' });
        this.tone(context, master, { start: now + 0.11, duration: 0.18, frequency: 880, endFrequency: 980, gain: 0.18, type: 'sine' });
        break;
      case 'mission-complete':
        this.sequence(context, master, now, [523.25, 659.25, 783.99], 0.095, 0.17, 'triangle');
        this.noise(context, master, now + 0.12, 0.12, 0.035, 2100);
        break;
      case 'quiz-correct':
        this.sequence(context, master, now, [740, 988], 0.105, 0.19, 'sine');
        this.tone(context, master, { start: now + 0.17, duration: 0.22, frequency: 1318.5, gain: 0.11, type: 'triangle' });
        break;
      case 'quiz-wrong':
        this.tone(context, master, { start: now, duration: 0.16, frequency: 286, endFrequency: 224, gain: 0.16, type: 'sine' });
        this.noise(context, master, now + 0.015, 0.12, 0.025, 700);
        break;
      case 'quiz-retry':
        this.tone(context, master, { start: now, duration: 0.13, frequency: 360, endFrequency: 420, gain: 0.11, type: 'sine' });
        this.tone(context, master, { start: now + 0.11, duration: 0.16, frequency: 560, endFrequency: 650, gain: 0.09, type: 'triangle' });
        break;
      case 'world-open':
        this.sequence(context, master, now, [392, 523.25, 659.25, 783.99], 0.105, 0.13, 'sine');
        this.noise(context, master, now + 0.18, 0.24, 0.034, 2500);
        break;
      case 'passport-stamp':
        this.noise(context, master, now, 0.055, 0.075, 720);
        this.tone(context, master, { start: now + 0.07, duration: 0.22, frequency: 440, endFrequency: 660, gain: 0.14, type: 'triangle' });
        this.tone(context, master, { start: now + 0.2, duration: 0.2, frequency: 880, gain: 0.08, type: 'sine' });
        break;
      case 'badge-unlock':
        this.sequence(context, master, now, [659.25, 783.99, 1046.5], 0.11, 0.15, 'triangle');
        this.sequence(context, master, now + 0.08, [987.77, 1318.51], 0.16, 0.075, 'sine');
        break;
      case 'fridge-store':
        this.tone(context, master, { start: now, duration: 0.11, frequency: 118, endFrequency: 82, gain: 0.2, type: 'sine' });
        this.tone(context, master, { start: now + 0.105, duration: 0.2, frequency: 520, endFrequency: 650, gain: 0.15, type: 'triangle' });
        this.tone(context, master, { start: now + 0.2, duration: 0.18, frequency: 880, gain: 0.1, type: 'sine' });
        break;
      case 'daily-complete':
        this.sequence(context, master, now, [523.25, 659.25, 783.99, 1046.5], 0.12, 0.17, 'triangle');
        this.sequence(context, master, now + 0.08, [783.99, 987.77, 1318.51], 0.14, 0.09, 'sine');
        this.noise(context, master, now + 0.24, 0.28, 0.045, 2600);
        break;
      case 'family-message':
        this.sequence(context, master, now, [392, 493.88, 587.33], 0.14, 0.15, 'sine');
        this.tone(context, master, { start: now + 0.24, duration: 0.26, frequency: 784, endFrequency: 740, gain: 0.09, type: 'triangle' });
        break;
      case 'family-cheer':
        this.sequence(context, master, now, [392, 523.25, 659.25], 0.13, 0.14, 'sine');
        this.tone(context, master, { start: now + 0.21, duration: 0.29, frequency: 784, endFrequency: 988, gain: 0.09, type: 'triangle' });
        this.noise(context, master, now + 0.25, 0.18, 0.024, 2200);
        break;
    }
    return true;
  }

  private durationFor(effect: SoundEffect): number {
    switch (effect) {
      case 'tap': return 0.08;
      case 'menu-open': return 0.13;
      case 'choice-select': return 0.2;
      case 'mission-start': return 0.3;
      case 'record-complete': return 0.34;
      case 'mission-complete': return 0.42;
      case 'quiz-correct': return 0.44;
      case 'quiz-wrong': return 0.24;
      case 'quiz-retry': return 0.31;
      case 'world-open': return 0.58;
      case 'passport-stamp': return 0.48;
      case 'badge-unlock': return 0.58;
      case 'fridge-store': return 0.44;
      case 'daily-complete': return 0.72;
      case 'family-message': return 0.64;
      case 'family-cheer': return 0.7;
    }
  }

  private tone(context: BrowserAudioContext, output: AudioNode, options: ToneOptions): void {
    const oscillator = context.createOscillator();
    const gain = context.createGain();
    oscillator.type = options.type ?? 'sine';
    oscillator.frequency.setValueAtTime(Math.max(30, options.frequency), options.start);
    if (options.endFrequency) oscillator.frequency.exponentialRampToValueAtTime(Math.max(30, options.endFrequency), options.start + options.duration);
    gain.gain.setValueAtTime(0.0001, options.start);
    gain.gain.exponentialRampToValueAtTime(Math.max(0.0001, options.gain), options.start + Math.min(0.018, options.duration / 3));
    gain.gain.exponentialRampToValueAtTime(0.0001, options.start + options.duration);
    oscillator.connect(gain);
    gain.connect(output);
    oscillator.start(options.start);
    oscillator.stop(options.start + options.duration + 0.02);
  }

  private sequence(context: BrowserAudioContext, output: AudioNode, start: number, frequencies: number[], step: number, gain: number, type: OscillatorType): void {
    frequencies.forEach((frequency, index) => {
      this.tone(context, output, { start: start + index * step, duration: step * 1.45, frequency, gain, type });
    });
  }

  private noise(context: BrowserAudioContext, output: AudioNode, start: number, duration: number, gainValue: number, cutoff: number): void {
    const frameCount = Math.max(1, Math.floor(context.sampleRate * duration));
    const buffer = context.createBuffer(1, frameCount, context.sampleRate);
    const channel = buffer.getChannelData(0);
    for (let index = 0; index < frameCount; index += 1) {
      const envelope = 1 - index / frameCount;
      channel[index] = (Math.random() * 2 - 1) * envelope;
    }
    const source = context.createBufferSource();
    const filter = context.createBiquadFilter();
    const gain = context.createGain();
    filter.type = 'lowpass';
    filter.frequency.setValueAtTime(cutoff, start);
    gain.gain.setValueAtTime(Math.max(0.0001, gainValue), start);
    gain.gain.exponentialRampToValueAtTime(0.0001, start + duration);
    source.buffer = buffer;
    source.connect(filter);
    filter.connect(gain);
    gain.connect(output);
    source.start(start);
  }
}

export const mrmSoundEngine = new MrmSoundEngine();
